/**
 * RealizedHero — Deal Detail hero card for owned properties.
 *
 * Batch F. Renders when `deal.is_owned === true` and the backend has
 * attached `deal.realized_metrics`. Shows the 6-cell metric grid +
 * a collapsible year-by-year table beneath it.
 *
 * Cell layout (top→bottom, left→right):
 *   ┌─────────────────────────────┬─────────────────────────────┐
 *   │ Total rent collected        │ Realized cash flow          │
 *   ├─────────────────────────────┼─────────────────────────────┤
 *   │ Principal paid down         │ Appreciation                │
 *   ├─────────────────────────────┼─────────────────────────────┤
 *   │ Equity built                │ Total return                │
 *   └─────────────────────────────┴─────────────────────────────┘
 *
 * Positive numbers render text-emerald-600, negative text-rose-600.
 * Total Return cell adds an annualised + % chip in its footer.
 */
import { useState } from "react";
import {
  ChevronDown, ChevronUp, Coins, PiggyBank, TrendingUp,
  Wallet, Trophy, Wallet2, Sparkles,
} from "lucide-react";
import { useFmt } from "@/lib/useFmt";

const VALUE_SOURCE_LABEL = {
  manual: "Manual entry",
  ai_estimate: "AI estimate",
  comp_data: "Comp data",
  unknown: "Unknown",
};

function Cell({ icon: Icon, label, value, color = "text-slate-900", testid, footer }) {
  return (
    <div data-testid={testid} className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
        {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${color}`}>{value}</div>
      {footer ? <div className="mt-0.5 text-xs text-slate-500">{footer}</div> : null}
    </div>
  );
}

function moneyOrDash(fmt, n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return fmt.money(Number(n));
}

function pctOrDash(n, digits = 1) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(digits)}%`;
}

export default function RealizedHero({ deal }) {
  const fmt = useFmt();
  const rm = deal?.realized_metrics || {};
  const [expanded, setExpanded] = useState(false);

  if (rm._status === "future_purchase") {
    return (
      <div data-testid="realized-future-warning" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
        <Sparkles className="mr-2 inline h-4 w-4" />
        Purchase year is in the future — realized performance unavailable. Update the purchase year to compute.
      </div>
    );
  }
  if (rm._status === "missing_inputs") {
    return (
      <div data-testid="realized-missing-inputs" className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        Enter the purchase year to see realized performance.
      </div>
    );
  }
  if (!rm._status) return null;

  const years = rm.years_owned ?? 0;
  const startYear = rm.purchase_year;
  const totalReturnColor = (rm.total_return_dollar ?? 0) >= 0 ? "text-emerald-600" : "text-rose-600";
  const cfColor = (rm.realized_cash_flow_total ?? 0) >= 0 ? "text-emerald-600" : "text-rose-600";
  const apprColor = (rm.appreciation_dollar ?? 0) >= 0 ? "text-emerald-600" : "text-rose-600";
  const visibleRows = expanded
    ? (rm.year_by_year || [])
    : (rm.year_by_year || []).slice(-5);

  return (
    <section data-testid="realized-hero" className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
            Realized performance
          </p>
          <h2 className="mt-0.5 text-xl font-bold text-slate-900">
            Held since {startYear || "—"} <span className="text-slate-400">·</span>{" "}
            <span className="text-slate-600">{years} year{years === 1 ? "" : "s"}</span>
          </h2>
        </div>
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200">
          Owned
        </span>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Cell
          testid="realized-cell-total-rent"
          icon={PiggyBank}
          label="Total rent collected"
          value={moneyOrDash(fmt, rm.cumulative_rent_received)}
          color="text-slate-900"
          footer={`over ${rm.years_rented ?? 0} year(s) of rental`}
        />
        <Cell
          testid="realized-cell-cash-flow"
          icon={Wallet}
          label="Realized cash flow"
          value={moneyOrDash(fmt, rm.realized_cash_flow_total)}
          color={cfColor}
          footer={`${moneyOrDash(fmt, rm.realized_cash_flow_annualized)} per year`}
        />
        <Cell
          testid="realized-cell-principal"
          icon={Coins}
          label="Principal paid down"
          value={moneyOrDash(fmt, rm.cumulative_principal_paid)}
          color="text-slate-900"
          footer={`balance now ${moneyOrDash(fmt, rm.current_loan_balance)}`}
        />
        <Cell
          testid="realized-cell-appreciation"
          icon={TrendingUp}
          label="Appreciation"
          value={moneyOrDash(fmt, rm.appreciation_dollar)}
          color={apprColor}
          footer={`${pctOrDash(rm.appreciation_percent)} · ${pctOrDash(rm.appreciation_annualized_percent, 2)}/yr`}
        />
        <Cell
          testid="realized-cell-equity"
          icon={Wallet2}
          label="Equity built"
          value={moneyOrDash(fmt, rm.equity_built)}
          color="text-slate-900"
          footer="appreciation + principal paid"
        />
        <Cell
          testid="realized-cell-total-return"
          icon={Trophy}
          label="Total return"
          value={moneyOrDash(fmt, rm.total_return_dollar)}
          color={totalReturnColor}
          footer={
            <>
              {pctOrDash(rm.total_return_percent)} on{" "}
              {moneyOrDash(fmt, rm.original_cash_invested)} invested
              {" · "}{pctOrDash(rm.total_return_annualized_percent)}/yr
            </>
          }
        />
      </div>

      {/* Valuation provenance footer */}
      <p className="mt-3 text-xs text-slate-500" data-testid="realized-valuation-source">
        Current valuation: {moneyOrDash(fmt, rm.current_estimated_value)} ·{" "}
        {VALUE_SOURCE_LABEL[rm.current_value_source || "manual"] || "Manual entry"}
        {rm.current_value_updated_at
          ? ` · updated ${new Date(rm.current_value_updated_at).toLocaleDateString()}`
          : ""}
      </p>

      {/* Year-by-year table */}
      {(rm.year_by_year || []).length > 0 && (
        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50">
          <button
            type="button"
            data-testid="realized-yearly-toggle"
            onClick={() => setExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-slate-900"
          >
            <span>Year-by-year detail</span>
            {expanded
              ? <ChevronUp className="h-4 w-4" aria-hidden="true" />
              : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
          </button>
          {/* Always show at least 5 rows for context */}
          <div className="overflow-x-auto border-t border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Year</th>
                  <th className="px-4 py-2 text-right">Avg rent / mo</th>
                  <th className="px-4 py-2 text-right">Annual cash flow</th>
                  <th className="px-4 py-2 text-right">Cumulative CF</th>
                </tr>
              </thead>
              <tbody data-testid="realized-yearly-table" className="divide-y divide-slate-100">
                {visibleRows.map((row) => {
                  const cfClr = (row.annual_cash_flow ?? 0) >= 0 ? "text-emerald-700" : "text-rose-700";
                  const ccfClr = (row.cumulative_cash_flow ?? 0) >= 0 ? "text-emerald-700" : "text-rose-700";
                  return (
                    <tr key={row.year} data-testid={`realized-row-${row.year}`}>
                      <td className="px-4 py-2 font-medium text-slate-700">{row.year}</td>
                      <td className="px-4 py-2 text-right text-slate-700 tabular-nums">{moneyOrDash(fmt, row.avg_monthly_rent)}</td>
                      <td className={`px-4 py-2 text-right tabular-nums ${cfClr}`}>{moneyOrDash(fmt, row.annual_cash_flow)}</td>
                      <td className={`px-4 py-2 text-right tabular-nums ${ccfClr}`}>{moneyOrDash(fmt, row.cumulative_cash_flow)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!expanded && (rm.year_by_year || []).length > 5 ? (
              <div className="border-t border-slate-100 px-4 py-2 text-center text-xs text-slate-500">
                Showing last 5 of {rm.year_by_year.length} years · click "Year-by-year detail" to expand
              </div>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
